const PRINT_STYLE_ID = "viewer-express-print-css";
const PRINT_TARGET_CLASS = "bve-print-target";
const PRINT_ANCESTOR_CLASS = "bve-print-ancestor";

/**
 * Hosts pin the viewer with high-specificity `!important` rules (e.g. dev-shell
 * `#root … .content-frame`). Each `:not(#id)` adds ID-level specificity, so print rules win on
 * the host's ancestor chain without knowing the host's selectors.
 */
const BOOST = ":not(#bve-p1):not(#bve-p2):not(#bve-p3)";
const ANCESTOR = `.${PRINT_ANCESTOR_CLASS}${BOOST}`;
const TARGET = `.${PRINT_TARGET_CLASS}`;

/**
 * Print-only rules: hide everything outside the viewer's ancestor chain, flatten that chain to
 * block layout (flex `flex-basis: 0` columns collapse once heights are auto), and lift every
 * fixed height / scroll clip so the full screen content flows across pages.
 *
 * The viewer's own elements get low-specificity rules (enough to beat the non-important base
 * container CSS) so theme `@media print` rules can still override them.
 */
const PRINT_CSS = `
@media print {
  ${ANCESTOR} > :not(.${PRINT_ANCESTOR_CLASS}):not(.${PRINT_TARGET_CLASS}) {
    display: none !important;
  }
  ${ANCESTOR} {
    display: block !important;
    flex: none !important;
    position: static !important;
    width: auto !important;
  }
  ${ANCESTOR}, ${TARGET}, ${TARGET} #viewer-express-container, ${TARGET} .stage-container {
    height: auto !important;
    min-height: 0 !important;
    max-height: none !important;
    overflow: visible !important;
    transform: none !important;
  }
}
`;

const getAncestors = (element: HTMLElement): HTMLElement[] => {
  const ancestors: HTMLElement[] = [];
  let current = element.parentElement;
  while (current) {
    ancestors.push(current);
    current = current.parentElement;
  }
  return ancestors;
};

/**
 * BV Express renders inline in the host document (no screen iframe like the full viewer), so a
 * plain `window.print()` would print the whole host shell. Scope the print job to `container`.
 */
export const printContainer = (container: HTMLElement | null): void => {
  if (!container) {
    console.error("Unable to print: viewer content is not mounted.");
    return;
  }

  const ancestors = getAncestors(container);

  const style = document.createElement("style");
  style.id = PRINT_STYLE_ID;
  style.textContent = PRINT_CSS;
  document.getElementById(PRINT_STYLE_ID)?.remove();
  // First in <head> so theme print rules of equal specificity (injected later) take precedence.
  document.head.prepend(style);

  container.classList.add(PRINT_TARGET_CLASS);
  ancestors.forEach((el) => el.classList.add(PRINT_ANCESTOR_CLASS));

  const cleanup = (): void => {
    window.removeEventListener("afterprint", cleanup);
    container.classList.remove(PRINT_TARGET_CLASS);
    ancestors.forEach((el) => el.classList.remove(PRINT_ANCESTOR_CLASS));
    style.remove();
  };

  window.addEventListener("afterprint", cleanup);
  window.print();
};
